import { Button, EmptyState } from "@nexus/ui";
import { useQuery } from "@tanstack/react-query";
import { downloadOrderScriptAsset } from "@/features/marketplace/api/marketplace.api";
import { toMarketplaceSearch } from "@/features/marketplace/lib/marketplace-search";
import { orderQueryOptions } from "@/lib/queries";

import { clearOrdersListFilters } from "../../lib/orders-list-model";
import { OrdersBulkBar } from "./OrdersBulkBar";
import { OrdersDetailPanel } from "./OrdersDetailPanel";
import { OrdersHubToolbar } from "./OrdersHubToolbar";
import { OrdersListGroups } from "./OrdersListGroups";
import { OrdersToolbar } from "./OrdersToolbar";
import {
  useOrdersListController,
  type OrdersListProps,
} from "./use-orders-list-controller";

export type {
  OrdersListNavigationMode,
  OrdersListProps,
  OrdersListVariant,
} from "./use-orders-list-controller";

export function OrdersList(props: OrdersListProps) {
  return (
    <TenantOrdersList
      key={`${props.tenantId}:${props.variant}:${props.navigationMode ?? "marketplace"}:${props.chrome ?? "marketplace"}:${props.stateScope ?? "tenant"}`}
      {...props}
    />
  );
}

function TenantOrdersList(props: OrdersListProps) {
  const {
    t,
    navigate,
    canEdit,
    listRef,
    searchInputRef,
    todayYmd,
    query,
    setQuery,
    expandedIds,
    selection,
    setSelection,
    setActiveRowId,
    panelWidth,
    setPanelWidth,
    exportPending,
    retryOrder,
    bulk,
    canInteract,
    visibleOrders,
    groups,
    vendors,
    attentionCount,
    bulkStatusOptions,
    liveAnnouncement,
    selectedVisibleCount,
    allVisibleSelected,
    pendingScriptIds,
    toggleExpanded,
    clearSelection,
    toggleAllVisible,
    handleScriptStatusChange,
    handleBulkStatus,
    handleExportExcel,
    openPanel,
    resolvedPanel,
    panelOrder,
    panelScript,
    treeRowIds,
    handleListKeyDown,
    rovingActiveId,
    groupLabel,
    tenantId,
    variant,
    chrome,
    orders,
  } = useOrdersListController(props);
  const fulfillmentGroup = variant === "in-production" ? "active" : variant;
  const { data: panelOrderDetail } = useQuery(
    orderQueryOptions(tenantId, panelOrder?.id, fulfillmentGroup),
  );
  const displayedPanelOrder = panelOrderDetail ?? panelOrder;
  const displayedPanelScript =
    resolvedPanel?.kind === "script" && displayedPanelOrder
      ? (displayedPanelOrder.scripts.find(
          (script) => script.id === resolvedPanel.scriptId,
        ) ?? panelScript)
      : null;

  return (
    <>
      <div
        ref={listRef}
        data-orders-list-layout
        className="flex min-w-0 flex-col gap-3"
        onKeyDown={handleListKeyDown}
      >
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {liveAnnouncement}
        </span>
        {chrome === "hub" ? (
          <OrdersHubToolbar
            query={query}
            orderCount={orders.length}
            attentionCount={attentionCount}
            onExport={
              variant === "in-production"
                ? () => void handleExportExcel()
                : undefined
            }
            exportDisabled={visibleOrders.length === 0}
            exportPending={exportPending}
            onChange={setQuery}
          />
        ) : (
          <div className="sticky top-0 z-20 -mx-1 bg-background/95 px-1 py-2 backdrop-blur-sm">
            <OrdersToolbar
              query={query}
              vendors={vendors}
              attentionCount={attentionCount}
              shownCount={visibleOrders.length}
              totalCount={orders.length}
              showAttentionFilter={variant === "in-production"}
              onExport={
                variant === "in-production"
                  ? () => void handleExportExcel()
                  : undefined
              }
              exportDisabled={visibleOrders.length === 0}
              exportPending={exportPending}
              onChange={setQuery}
              searchInputRef={searchInputRef}
            />
          </div>
        )}
        {selection.size > 0 ? (
          <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-3xl shadow-lg">
            <OrdersBulkBar
              scriptCount={selection.size}
              statusOptions={bulkStatusOptions}
              applying={bulk.applying}
              onSetStatus={(status) => void handleBulkStatus(status)}
              onClear={clearSelection}
            />
          </div>
        ) : null}

        {visibleOrders.length === 0 ? (
          <EmptyState
            icon="search"
            title={t("marketplace.orders.list.emptyFiltered.title")}
            description={t("marketplace.orders.list.emptyFiltered.description")}
            className="rounded-md border border-dashed border-border py-10"
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setQuery(clearOrdersListFilters(query))}
              >
                {t("marketplace.orders.list.clearFilters")}
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-md">
            <OrdersListGroups
              t={t}
              chrome={chrome}
              query={query}
              setQuery={setQuery}
              canInteract={canInteract}
              allVisibleSelected={allVisibleSelected}
              selectedVisibleCount={selectedVisibleCount}
              toggleAllVisible={toggleAllVisible}
              groups={groups}
              groupLabel={groupLabel}
              todayYmd={todayYmd}
              expandedIds={expandedIds}
              resolvedPanel={resolvedPanel}
              treeRowIds={treeRowIds}
              rovingActiveId={rovingActiveId}
              selection={selection}
              setSelection={setSelection}
              pendingScriptIds={pendingScriptIds}
              retryOrder={retryOrder}
              tenantId={tenantId}
              variant={variant}
              toggleExpanded={toggleExpanded}
              setActiveRowId={setActiveRowId}
              handleScriptStatusChange={handleScriptStatusChange}
              onViewOrderHistory={(orderId) => {
                const destinationTab =
                  orders.find((order) => order.id === orderId)
                    ?.fulfillmentStatus === "CANCELLED"
                    ? "archive"
                    : "history";
                void navigate({
                  to: "/marketplace",
                  search: (previous) => ({
                    ...toMarketplaceSearch(previous),
                    tab: destinationTab,
                    order: orderId,
                    script: undefined,
                  }),
                });
              }}
              openPanel={openPanel}
            />
          </div>
        )}

        {chrome === "hub" ? null : (
        <p
          className="hidden text-[11px] text-muted-foreground lg:block"
          aria-hidden
        >
          {t("marketplace.orders.list.keyboardHint")}
        </p>
        )}
      </div>

      {resolvedPanel && displayedPanelOrder ? (
        <OrdersDetailPanel
          target={resolvedPanel}
          order={displayedPanelOrder}
          script={displayedPanelScript}
          todayYmd={todayYmd}
          tenantId={tenantId}
          variant={variant}
          canEdit={canInteract}
          canViewActivity
          canAddComment={canEdit}
          activityAudience="client"
          width={panelWidth}
          layout={chrome === "hub" ? "hub" : "resizable"}
          pendingScriptIds={pendingScriptIds}
          onWidthChange={setPanelWidth}
          onClose={() => openPanel(null)}
          onTargetChange={openPanel}
          onStatusChange={(scriptId, next) =>
            handleScriptStatusChange(displayedPanelOrder, scriptId, next)
          }
          onDownloadAsset={(script) =>
            downloadOrderScriptAsset(
              displayedPanelOrder.id,
              script.id,
              tenantId,
            )
          }
          onViewHistory={() => {
            const destinationTab =
              displayedPanelOrder.fulfillmentStatus === "CANCELLED"
                ? "archive"
                : "history";
            void navigate({
              to: "/marketplace",
              search: (previous) => ({
                ...toMarketplaceSearch(previous),
                tab: destinationTab,
                order: displayedPanelOrder.id,
                script: undefined,
              }),
            });
          }}
        />
      ) : null}
    </>
  );
}
